import { LoadingButton, TabContext, TabList, TabPanel } from '@mui/lab';
import { Alert, Box, Container, Stack, Tab } from '@mui/material';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useActiveSimulation } from '../../active-simulation/useActiveSimulation';
import { useStartSimulation } from '../../active-simulation/useStartSimulation';
import { useExercise, useSimulations } from '../../api';
import AppBar from '../../app-bar/AppBar';
import ksimConsole from '../../assets/ksim-console.png';
import ksimConsole2 from '../../assets/ksim-console2.png';
import ksimUnhappy from '../../assets/ksim-unhappy.png';
import AssignmentDetails from '../../assignment-details/AssignmentDetails';
import { useCheckRole } from '../../auth';
import BackLink from '../../back-link/BackLink';
import Breadcrumbs from '../../breadcrumbs/Breadcrumbs';
import Briefing from '../../briefing/Briefing';
import DueMessage from '../../due-message/DueMessage';
import ExerciseHeader from '../../exercise-header/ExerciseHeader';
import Fallback from '../../fallback/Fallback';
import KSimHeader from '../../k-sim-header/KSimHeader';
import SimulationDisabledDialog from '../../simulation-disabled-dialog/SimulationDisabledDialog';
import SimulationErrorDialog from '../../simulation-error-dialog/SimulationErrorDialog';
import SimulationSummaryCard from '../../simulation-summary-card/SimulationSummaryCard';
import SimulatorCard from '../../simulator-card/SimulatorCard';
import ExerciseSkeleton from '../../skeletons/ExerciseSkeleton';
import {
  ApiExercise,
  Exercise,
  ExerciseState,
  ExerciseType,
  LoadingSimulation,
  SimulationRole,
  SimulationSummary,
} from '../../types';
import { useDesktop } from '../../useDesktop';
import { parseTime } from '../../utils/parseTime';

const SimulationsLabel = ({ attempts, simulations }: { attempts?: number; simulations?: SimulationSummary[] }) => {
  if (!simulations) {
    return <>Simulations</>;
  }

  return (
    <>
      Simulations ({simulations.length}
      {attempts ? ` of ${attempts}` : ''})
    </>
  );
};

const ExerciseDetail = () => {
  useCheckRole(['Student', 'Instructor']);

  const [selectedTab, selectTab] = useState('briefing');
  const onTabChange = (event: React.SyntheticEvent, newValue: string) => selectTab(newValue);
  const { exerciseId } = useParams();
  const isDesktop = useDesktop();

  const { activeSimulation, setActiveSimulation } = useActiveSimulation();

  const { exercise, isLoadingExercise, exerciseError } = useExercise(exerciseId);
  const { simulations, isLoadingSimulations, simulationsError } = useSimulations(exerciseId);

  const { startSimulation, simulationStarting, startSimulationError } = useStartSimulation({
    exerciseId: exerciseId!,
    assignmentGroupId: null,
    onSuccess: data => {
      const loadingSimulation: LoadingSimulation = {
        exercise: { id, name: exerciseFile.name, type },
        id: data.sessionId,
        message: `Launching ${exerciseFile.name ?? 'exercise'}`,
        progress: 0,
        role: SimulationRole.Instructor,
        simulator: {
          type: simulator.type,
        },
      };

      setActiveSimulation(loadingSimulation);
    },
    onError() {
      setActiveSimulation(null);
    },
  });

  if (isLoadingExercise) {
    return <ExerciseSkeleton />;
  }

  if (exerciseError || !exercise) {
    throw exerciseError;
  }

  const mappedExercise: Exercise = parseTime<ApiExercise>(exercise);
  const mappedSimulations: SimulationSummary[] | undefined = simulations?.map(parseTime);

  const {
    brief,
    endTime,
    id,
    maxNumberOfAttempts,
    exercise: exerciseFile,
    simulationDisabledReason,
    simulator,
    title,
    type,
  } = mappedExercise;
  // Assume that maxNumberOfAttempts won't be 0 because it would be handled as Unlimited
  const attemptsLeft = simulations && exercise && (!maxNumberOfAttempts || maxNumberOfAttempts > simulations.length);

  const startSimulationDisabled = !attemptsLeft || !!(activeSimulation || simulationDisabledReason);

  return (
    <>
      <AppBar title="Exercise" />
      <Breadcrumbs
        items={[{ label: 'Home', to: '/' }, { label: exerciseFile.name }]}
        sx={{ display: { xs: 'none', sm: 'flex' } }}
      />
      <Container>
        <BackLink label="Back to Home" sx={{ display: { xs: 'inline-block', sm: 'none' }, my: 2 }} to="/" />
        <KSimHeader
          adHoc={exerciseFile.adHoc}
          exerciseType={type}
          simulatorType={simulator.type}
          sx={{ mt: 1, mb: { xs: 2, sm: 4 } }}
        />

        <ExerciseHeader
          name={exerciseFile.name}
          simulatorType={simulator.type}
          title={title}
          type={type}
          startButton={
            exercise.type === ExerciseType.SelfStudy && (
              <Box sx={{ display: { xs: 'none', sm: 'flex' }, ml: 'auto' }}>
                {simulationDisabledReason && <SimulationDisabledDialog reason={simulationDisabledReason} />}
                <LoadingButton
                  disabled={startSimulationDisabled}
                  loading={simulationStarting}
                  variant="contained"
                  onClick={() => startSimulation()}
                >
                  Start simulation
                </LoadingButton>
              </Box>
            )
          }
        />

        {exerciseFile?.state === ExerciseState.Active && <DueMessage endTime={endTime} />}

        <Box sx={{ display: { sm: 'grid' }, gridTemplateColumns: '6fr minmax(0, 4fr)', gap: 11, mt: 3 }}>
          <Box pt={{ sm: 6 }} mb={3} gridColumn={2}>
            <AssignmentDetails {...mappedExercise} sx={{ mb: 2 }} />
            <SimulatorCard
              adHoc={exerciseFile.adHoc}
              exerciseType={type}
              name={simulator.name}
              simulatorType={simulator.type}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            />
          </Box>

          {!activeSimulation && exercise.type === ExerciseType.SelfStudy && (
            <Box display={{ xs: 'flex', sm: 'none' }} justifyContent="center" mb={3}>
              <LoadingButton
                disabled={startSimulationDisabled}
                loading={simulationStarting}
                variant="contained"
                onClick={() => startSimulation()}
              >
                Start simulation
              </LoadingButton>
            </Box>
          )}

          <Box gridRow={1}>
            <TabContext value={selectedTab}>
              <TabList
                onChange={onTabChange}
                sx={{ borderBottom: 1, borderColor: 'divider' }}
                variant={isDesktop ? 'standard' : 'fullWidth'}
              >
                <Tab label="Briefing" value="briefing" />
                <Tab
                  label={<SimulationsLabel attempts={maxNumberOfAttempts} simulations={mappedSimulations} />}
                  value="simulations"
                />
              </TabList>
              <TabPanel value="briefing">
                {brief.length ? (
                  <Briefing sections={brief} />
                ) : (
                  <Fallback imgSrc={ksimConsole2} message="There is no briefing for this exercise" />
                )}
              </TabPanel>
              <TabPanel value="simulations">
                {isLoadingSimulations && <>Loading...</>}
                {simulationsError ? (
                  <Fallback
                    imgSrc={ksimUnhappy}
                    message={
                      <Alert severity="error">
                        Something went wrong and your simulations couldn&lsquo;t be loaded. Please try again
                      </Alert>
                    }
                  />
                ) : (
                  mappedSimulations &&
                  (mappedSimulations.length ? (
                    <Stack gap={2}>
                      {mappedSimulations.map(simulation => (
                        <SimulationSummaryCard
                          key={simulation.id}
                          showDetails
                          to={`/learn/exercises/${exercise.id}/simulations/${simulation.id}`}
                          {...simulation}
                        />
                      ))}
                    </Stack>
                  ) : (
                    <Fallback imgSrc={ksimConsole} message="No simulations have been run yet" />
                  ))
                )}
              </TabPanel>
            </TabContext>
          </Box>
        </Box>
      </Container>

      <SimulationErrorDialog error={startSimulationError ?? undefined} retry={startSimulation} />
    </>
  );
};

export default ExerciseDetail;
